import moment from 'moment'
import React from 'react'
import { ResourceItem } from '../style'

//* utils
import { ModuleNames } from '../../../../../constants/componentNames.constants'
import { OPTIONS } from '../../../../../constants/dateOptions'
import { LocaleCodes } from '../../../../../constants/localization.constants'
import { VideoPlayIcon } from '../../../../../constants/svg'
import { isResourceCategory } from '../../../../../utils/functions.utils'
import ImgUtil from '../../../../../utils/img.utils'
import { isExternalURL } from '../../../../../utils/link.utils'
import formatInlineCode from '../../../../../utils/shortTextFormatting'
import UrlUtil from '../../../../../utils/url.utils'
//* components
import Typography from '../../../../global/typography'
import WrapperImage from '../../../../global/wrapperImage'

export const ResourceCard = (
  item: any,
  i: number,
  isEvents: boolean,
  microcopyFeaturedText: string,
  microcopyOnDemand?: string,
  inEnglishText?: string,
  locale: string = LocaleCodes.ENGLISH.code
) => {
  const itemData = {
    title: item.title || '',
    teaser: {
      url: item.teaserImage || '',
      alt: item.teaserAlt || '',
      body: item.teaserBody || '',
    },
    slug: item.slug || '',
    body: item.body || '',
    icon: item.icon || '',
    altText: item.iconAlt || '',
    useCase: item.useCase || '',
    date: item.date || '',
    inEnglish: item.inEnglish && locale !== LocaleCodes.ENGLISH.code,
    category: {
      name: item.category || '',
      number: item.categoryNumber || '',
    },
    customer: {
      url: item.CustomerLogo?.image?.file?.url || '',
      alt: item.CustomerLogo?.altText || 'UiPath Customer Logo',
    },
    featured: item.featured || '',
  }

  let isOnDemand

  if (itemData.useCase === 'On Demand') {
    isOnDemand = true
  } else if (
    item[ModuleNames.TOPIC_WEBINAR_TYPE] &&
    Array.isArray(item[ModuleNames.TOPIC_WEBINAR_TYPE]) &&
    item[ModuleNames.TOPIC_WEBINAR_TYPE].includes('On-Demand')
  ) {
    isOnDemand = true
  } else if (
    item[ModuleNames.TOPIC_EVENT_TYPE] &&
    Array.isArray(item[ModuleNames.TOPIC_EVENT_TYPE]) &&
    item[ModuleNames.TOPIC_EVENT_TYPE].includes('On-Demand')
  ) {
    isOnDemand = true
  }

  const isVideoDemo = isResourceCategory('Video Demo', itemData.category.number)
  const isCaseStudy = isResourceCategory('Case Study', itemData.category.number)
  const resourceTitle = itemData.title ? itemData.title.replace(/<br\s*\\?>/g, '\r\n') : ''

  let resourceSubtitle = ''
  if (itemData.teaser.body || itemData.body) {
    if (itemData.teaser.body) {
      if (itemData.teaser.body?.length > 180) {
        resourceSubtitle = itemData.teaser.body.substr(0, 180) + '...'
      } else if (itemData.teaser.body) {
        resourceSubtitle = itemData.teaser.body
      }
    }
  }

  let convertedDate
  let resourceItemDate

  const datePassed = itemData.date && new Date(itemData.date) >= new Date()
  if (itemData.date) {
    resourceItemDate = moment(itemData.date.split('T')[0], 'YYYY-MM-DD')
      .utc()
      .format('MMMM DD, YYYY')
    resourceItemDate = new Date(resourceItemDate).toLocaleDateString(locale, OPTIONS)

    if (datePassed) {
      convertedDate = resourceItemDate
    }
  }

  let atr: any = {}

  if (UrlUtil.isValidURL(itemData.slug)) {
    atr = {
      href: itemData.slug,
      target: isExternalURL(itemData.slug) ? '_blank' : '_self',
    }
  } else {
    const newSlug = `/${itemData.slug.charAt(0) === '/' ? itemData.slug.slice(1) : itemData.slug}`
    atr = {
      href: newSlug,
      target: isExternalURL(newSlug) ? '_blank' : '_self',
    }
  }

  const resource = (
    <ResourceItem
      {...atr}
      key={`ResourceItem--${itemData.slug}-${i}`}
      $isEvents={isEvents}
      $isVideoDemo={isVideoDemo}>
      <div className="ResourceItem-Img-Container">
        <div className="ResourceItem-Pills-Container">
          {isOnDemand && (
            <div className="ResourceItem-Pill">
              <Typography type="microAlt">{microcopyOnDemand}</Typography>
            </div>
          )}

          {itemData.inEnglish && (
            <div className="ResourceItem-Pill">
              <Typography type="microAlt">{inEnglishText}</Typography>
            </div>
          )}
        </div>

        <div className="ResourceItem-Img-Overlay"></div>
        {itemData.icon && !isCaseStudy && !isVideoDemo && (
          <WrapperImage
            className="ResourceItem__Item-Icon"
            loading="lazy"
            src={ImgUtil.getResizedImageUrl(itemData.icon, '')}
            alt={itemData.altText}
            width={48}
            height={48}
            threshold={650}
          />
        )}

        {isVideoDemo && <VideoPlayIcon className="ResourceItem__Item-Icon" />}
        {isCaseStudy && itemData.customer.url && (
          <WrapperImage
            className="ResourceItem__Item-Case-Study-Logo"
            loading="lazy"
            src={ImgUtil.getResizedImageUrl(itemData.customer.url, '')}
            alt={itemData.customer.alt}
            width={175}
            height={88}
            threshold={650}
          />
        )}

        {itemData.teaser.url && (
          <WrapperImage
            className="ResourceItem-Img"
            src={ImgUtil.getResizedImageUrl(itemData.teaser.url, 'width=600')}
            loading="lazy"
            alt={itemData.teaser.alt}
            width={282}
            height={158}
            threshold={650}
          />
        )}
      </div>

      <div className="ResourceItem-Text">
        <div className="ResourceItem-Text-Category-Date">
          <div className="ResourceItemCategoryTextContainer">
            {itemData.featured && i < 6 && microcopyFeaturedText && (
              <Typography className="ResourceItem-Text-Featured" type="microAlt">
                {microcopyFeaturedText}
              </Typography>
            )}

            {itemData.category.name && (
              <Typography className="ResourceItem-Text-Category" type="captionAlt">
                {itemData.category.name}
              </Typography>
            )}
          </div>

          {convertedDate && (
            <Typography className="ResourceItem-Text-Date" type="caption">
              {convertedDate}
            </Typography>
          )}
        </div>

        {resourceTitle && (
          <Typography className="ResourceItem-Title" type="header6">
            <span> {formatInlineCode(resourceTitle)}</span>
          </Typography>
        )}

        {resourceSubtitle && (
          <Typography className="ResourceItem-Subtitle" type="bodyXS">
            {resourceSubtitle}
          </Typography>
        )}
      </div>
    </ResourceItem>
  )

  return resource
}
